import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Sparkles, BookOpenCheck } from "lucide-react";

// In-app phone mockup that mirrors the actual TNPSC Tamil app dashboard.
const AppMockup = () => {
  const { t, lang } = useLanguage();
  return (
    <div className="phone-frame w-[280px] sm:w-[300px] mx-auto float-anim">
      <div className="phone-screen h-[600px] sm:h-[620px]">
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold text-slate-700">
          <span>10:52</span>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-2 rounded-sm bg-slate-700/80" />
            <span className="inline-block w-3 h-2 rounded-sm bg-slate-700/80" />
            <span className="inline-block w-5 h-2.5 rounded-sm bg-emerald-500 text-[8px] text-white text-center leading-[10px]">
              63
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="px-5 pt-2">
          <div className="flex items-start justify-between">
            <div>
              <h3
                className="text-[20px] font-extrabold leading-tight text-blue-950"
                style={{
                  fontFamily:
                    lang === "ta"
                      ? "Noto Sans Tamil, sans-serif"
                      : "Outfit, sans-serif",
                }}
              >
                {lang === "ta" ? "வணக்கம், Narain!" : "Hello, Narain!"}
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {lang === "ta"
                  ? "இன்றைய பயிற்சியைத் தொடங்குக"
                  : "Start today's practice"}
              </p>
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-white border border-blue-900/10 px-1 py-0.5 text-[10px] font-semibold">
              <span
                className={`px-2 py-0.5 rounded-full ${
                  lang === "ta" ? "bg-blue-900 text-white" : "text-slate-500"
                }`}
              >
                தமிழ்
              </span>
              <span
                className={`px-2 py-0.5 rounded-full ${
                  lang === "en" ? "bg-blue-900 text-white" : "text-slate-500"
                }`}
              >
                EN
              </span>
            </div>
          </div>
        </div>

        {/* Exam chips */}
        <div className="px-5 mt-3 flex gap-2">
          <button className="px-3 py-1.5 rounded-full bg-blue-900 text-white text-[11px] font-bold">
            {lang === "ta" ? "குரூப் 4" : "Group 4"}
          </button>
          <button className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-700">
            {lang === "ta" ? "குரூப் 2" : "Group 2"}
          </button>
          <button className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[11px] font-bold text-slate-700">
            TNUSRB
          </button>
        </div>

        {/* My subscriptions card */}
        <div className="mx-5 mt-3 rounded-2xl bg-white border border-blue-900/10 p-3 shadow-sm">
          <div className="flex items-center justify-between mb-2.5">
            <span
              className="text-[10px] font-bold tracking-wider uppercase text-blue-900"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {lang === "ta" ? "என் சந்தாக்கள்" : "My subscriptions"}
            </span>
            <span className="text-[9px] text-amber-600 font-semibold">
              {lang === "ta" ? "நிர்வகி ›" : "Manage ›"}
            </span>
          </div>
          {[
            { name: lang === "ta" ? "குரூப் 4" : "Group 4", days: "7" },
            { name: lang === "ta" ? "குரூப் 2 / 2A" : "Group 2 / 2A", days: "7" },
          ].map((s) => (
            <div
              key={s.name}
              className="flex items-center justify-between py-1.5"
            >
              <div className="flex items-center gap-2">
                <span className="h-5 w-5 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <div className="leading-tight">
                  <p className="text-[12px] font-bold text-slate-900">
                    {s.name}
                  </p>
                  <p className="text-[9px] text-slate-500">
                    {lang === "ta" ? "இலவச முயற்சி" : "Free trial"}
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full">
                {s.days} {lang === "ta" ? "நாட்கள்" : "days left"}
              </span>
            </div>
          ))}
        </div>

        {/* Current affairs banner */}
        <div className="mx-5 mt-3 rounded-2xl overflow-hidden bg-gradient-to-r from-slate-900 to-blue-950 p-3 relative">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-amber-500/20 inline-flex items-center justify-center text-amber-400">
              <BookOpenCheck size={18} />
            </div>
            <div className="flex-1">
              <p className="text-[11px] font-bold text-amber-400">
                {lang === "ta"
                  ? "மே 2026 நடப்பு நிகழ்வுகள் சேர்க்கப்பட்டது"
                  : "May 2026 Current Affairs"}
              </p>
              <p className="text-[9px] text-slate-300 mt-0.5">
                40 {lang === "ta" ? "புதிய வினாக்கள்" : "fresh questions"}
              </p>
            </div>
            <span className="h-6 w-6 rounded-full bg-amber-500 text-slate-900 inline-flex items-center justify-center text-[10px] font-bold">
              →
            </span>
          </div>
        </div>

        {/* Quick start */}
        <div className="px-5 mt-3">
          <p className="text-[11px] font-bold text-slate-800 mb-2">
            {lang === "ta" ? "விரைவு தொடக்கம்" : "Quick start"}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {[
              {
                bg: "bg-amber-100",
                icon: "⚡",
                label: lang === "ta" ? "தினசரி" : "Daily",
                sub: "75 Q",
              },
              {
                bg: "bg-blue-100",
                icon: "🏆",
                label: lang === "ta" ? "வாரம்" : "Weekly",
                sub: "100 Q",
              },
              {
                bg: "bg-emerald-100",
                icon: "📚",
                label: lang === "ta" ? "பாடம்" : "Subject",
                sub: "10 Q",
              },
            ].map((q) => (
              <div
                key={q.label}
                className="rounded-xl bg-white border border-blue-900/5 p-2.5"
              >
                <div
                  className={`h-7 w-7 rounded-lg ${q.bg} text-base flex items-center justify-center`}
                >
                  {q.icon}
                </div>
                <p className="text-[10px] font-bold mt-1.5 text-slate-900">
                  {q.label}
                </p>
                <p className="text-[8px] text-slate-500">{q.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom nav */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 grid grid-cols-5 py-2 text-[9px] font-semibold text-slate-400">
          {[
            { l: lang === "ta" ? "முகப்பு" : "Home", active: true },
            { l: lang === "ta" ? "தேர்வுகள்" : "Tests" },
            { l: "AI" },
            { l: lang === "ta" ? "ஆய்வு" : "Analytics" },
            { l: lang === "ta" ? "சுயவிவரம்" : "Profile" },
          ].map((n) => (
            <div
              key={n.l}
              className={`flex flex-col items-center gap-0.5 ${
                n.active ? "text-blue-900" : ""
              }`}
            >
              <span className="h-4 w-4 rounded-md bg-current opacity-70" />
              <span>{n.l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useLanguage();
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 pb-20 sm:pt-36 sm:pb-24 lg:pt-40 lg:pb-28 overflow-hidden kolam-bg"
    >
      {/* Backdrop gopuram watermark */}
      <div
        className="absolute -top-20 -right-32 w-[600px] h-[600px] opacity-[0.05] pointer-events-none rotate-12"
        aria-hidden
      >
        <svg viewBox="0 0 200 200" fill="#1E3A8A">
          <path d="M100 10 L80 30 L80 50 L60 60 L60 80 L40 90 L40 120 L20 130 L20 180 L180 180 L180 130 L160 120 L160 90 L140 80 L140 60 L120 50 L120 30 Z" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <span
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-amber-700 bg-amber-100/70 border border-amber-300/60 px-3.5 py-1.5 rounded-full"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={14} className="text-amber-600" />
              {t.hero.eyebrow}
            </span>

            <div
              data-testid="hero-presents"
              className="mt-4 inline-flex items-center gap-2.5 rounded-full bg-white border border-blue-900/10 pl-1.5 pr-4 py-1.5 shadow-sm"
            >
              <img
                src="/images/vetri-guide-logo.png"
                alt="Vetri Guide"
                className="h-7 w-7 rounded-full object-contain"
              />
              <span className="text-[12px] leading-tight">
                <span className="text-slate-500 font-medium">
                  {t.common.presents}
                </span>{" "}
                <span
                  className="font-black text-blue-950"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {t.common.appName}
                </span>{" "}
                <span className="text-slate-400">— {t.common.freeTrial}</span>
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="mt-6 text-[36px] sm:text-[52px] lg:text-[64px] leading-[1.02] tracking-tight font-black text-blue-950"
            >
              <span className="block">{t.hero.titleA}</span>
              <span className="block text-gradient-gold">{t.hero.titleB}</span>
              <span className="block text-slate-900">{t.hero.titleC}</span>
            </h1>

            <p
              className="mt-7 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl"
              data-testid="hero-subtitle"
            >
              {t.hero.subtitle}
            </p>

            <div
              className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              id="download"
            >
              <div className="pulse-gold rounded-2xl">
                <a
                  data-testid="hero-cta-playstore"
                  href="https://play.google.com/store/apps/details?id=com.vetriguide.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-shine inline-flex items-center gap-3 rounded-2xl px-7 py-4 bg-slate-900 text-white font-bold shadow-xl shadow-slate-900/25 hover:-translate-y-0.5 transition"
                >
                  <svg viewBox="0 0 24 24" className="h-7 w-7" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.6 1.7a1.5 1.5 0 0 0-.6 1.2v18.2c0 .5.2.9.6 1.2L13.2 12 3.6 1.7z" fill="#34A853"/>
                    <path d="M16.8 8.5 13.2 12l3.6 3.5 4.2-2.4c1-.6 1-2.1 0-2.7L16.8 8.5z" fill="#FBBC04"/>
                    <path d="M3.6 22.3c.3.2.7.3 1.1.1l12.1-6.9-3.6-3.5L3.6 22.3z" fill="#EA4335"/>
                    <path d="M3.6 1.7 13.2 12l3.6-3.5L4.7 1.6c-.4-.2-.8-.1-1.1.1z" fill="#4285F4"/>
                  </svg>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-[10px] tracking-wider uppercase font-semibold text-slate-300">
                      {t.common.downloadPlayStoreSub}
                    </span>
                    <span style={{ fontFamily: "Outfit, sans-serif" }}>
                      {t.hero.ctaPrimary}
                    </span>
                  </span>
                </a>
              </div>
              <a
                href="#features"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 font-semibold text-blue-950 hover:bg-blue-900/5 transition"
              >
                {t.hero.ctaSecondary}
                <span className="inline-block">→</span>
              </a>
            </div>

            <div className="mt-7 flex items-center gap-3 text-sm text-slate-600">
              <div className="flex -space-x-2">
                {["#1E3A8A", "#EAB308", "#991B1B", "#0F766E"].map((c, i) => (
                  <div
                    key={i}
                    className="h-8 w-8 rounded-full border-2 border-[#fdfbf7] flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ background: c }}
                  >
                    {["M", "N", "P", "K"][i]}
                  </div>
                ))}
              </div>
              <span data-testid="hero-trustline">{t.hero.trustLine}</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            {/* Decorative ring */}
            <div className="absolute -inset-10 rounded-full bg-gradient-to-br from-amber-200/40 via-transparent to-blue-300/30 blur-3xl pointer-events-none" />
            <div className="relative">
              <AppMockup />
              {/* Floating trial badge */}
              <div className="absolute -left-6 top-16 bg-white rounded-2xl border border-blue-900/10 shadow-lg px-3.5 py-2.5 flex items-center gap-2.5 rotate-[-4deg] hidden sm:flex">
                <div className="h-9 w-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-base font-bold">
                  ✓
                </div>
                <div className="leading-tight">
                  <p className="text-xs font-bold text-slate-900">
                    {t.common.freeTrial}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    No card required
                  </p>
                </div>
              </div>
              {/* Floating MCQ badge */}
              <div className="absolute -right-2 bottom-24 bg-blue-950 text-white rounded-2xl shadow-xl px-3.5 py-2.5 flex items-center gap-2.5 rotate-[5deg] hidden sm:flex">
                <span className="text-xl font-black text-amber-400">16K+</span>
                <span className="text-[11px] leading-tight">
                  MCQs
                  <br />
                  curated
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
